/** @typedef {import('./projectPermissions').ProjectPermissions} ProjectPermissions */

/**
 * Canonical project-permission vocabulary. Use these everywhere instead of bare
 * "invoice" / "view" strings: a typo'd constant is a loud ReferenceError, whereas
 * a typo'd string silently returns "not granted" and the UI just disappears.
 *
 * These MIRROR the backend constants in
 * dlc_backend/config/projectPermissionConstants.js
 * (PROJECT_PERMISSION_AREAS / PROJECT_PERMISSION_ACTIONS). The two packages share
 * no code, so the values must be kept identical by hand.
 */
export const PROJECT_AREA = Object.freeze({
	OVERVIEW: "overview",
	PLANNING: "planning",
	COST_TRACKING: "costTracking",
	LV: "lv",
	INVOICE: "invoice",
	TICKET_CATEGORIES: "ticketCategories",
	TICKETS: "tickets",
	MEMBERS: "members",
	COMPANIES: "companies",
});

export const PROJECT_ACTION = Object.freeze({
	VIEW: "view",
	EDIT: "edit",
	CREATE: "create",
	DELETE: "delete",
	UPLOAD: "upload",
	FINALIZE: "finalize",
	MANAGE_AUFMASS: "manageAufmass",
	MANAGE_SUPPLIER_INFO: "manageSupplierInfo",
	INVITE: "invite",
	ADD: "add",
	EDIT_PERMISSIONS: "editPermissions",
	REMOVE: "remove",
	REVOKE: "revoke",
	APPROVE: "approve",
	EXPORT: "export",
	DOWNLOAD: "download",
});

// Valid-value sets backing the dev-time typo guard in useProjectPermission.can().
export const PROJECT_AREA_VALUES = new Set(Object.values(PROJECT_AREA));
export const PROJECT_ACTION_VALUES = new Set(Object.values(PROJECT_ACTION));

/**
 * Permission VALUE vocabularies - the level a view/edit/scope can take. These
 * MIRROR the backend constants in dlc_backend/config/projectPermissionConstants.js
 * (TICKET_ACCESS_LEVELS / CATEGORY_ACCESS_LEVELS / OVERVIEW_LEVELS). Use these
 * instead of bare "NONE" / "ALL" / "OWN" comparisons, for the same reason as
 * PROJECT_AREA / PROJECT_ACTION: a typo'd string silently flips a gate.
 */
export const TICKET_ACCESS_LEVEL = Object.freeze({
	ALL: "ALL",
	ASSIGNED_AND_OWN: "ASSIGNED_AND_OWN",
	OWN: "OWN",
	NONE: "NONE",
});

export const CATEGORY_ACCESS = Object.freeze({
	ALL: "ALL",
	NONE: "NONE",
});

// Overview view-level: every member sees at least the basic overview (identity +
// status counts); OVERVIEW_FINANCIALS additionally unlocks the financial amounts.
export const OVERVIEW_LEVEL = Object.freeze({
	OVERVIEW: "OVERVIEW",
	OVERVIEW_FINANCIALS: "OVERVIEW_FINANCIALS",
});

// Ordered lists for dropdowns, derived from the value vocabularies above.
export const TICKET_VIEW_LEVELS = [
	TICKET_ACCESS_LEVEL.ALL,
	TICKET_ACCESS_LEVEL.ASSIGNED_AND_OWN,
	TICKET_ACCESS_LEVEL.OWN,
	TICKET_ACCESS_LEVEL.NONE,
];

export const TICKET_EDIT_LEVELS = [
	TICKET_ACCESS_LEVEL.ALL,
	TICKET_ACCESS_LEVEL.OWN,
	TICKET_ACCESS_LEVEL.NONE,
];

export const CATEGORY_ACCESS_LEVELS = [CATEGORY_ACCESS.ALL, CATEGORY_ACCESS.NONE];

export const OVERVIEW_LEVELS = [
	OVERVIEW_LEVEL.OVERVIEW,
	OVERVIEW_LEVEL.OVERVIEW_FINANCIALS,
];

const MEMBER_PERMISSIONS = {
	overview: { view: OVERVIEW_LEVEL.OVERVIEW, edit: false },
	planning: { view: true, edit: true, export: true },
	costTracking: {
		view: true,
		edit: true,
		export: true,
	},
	lv: {
		view: true,
		create: true,
		edit: true,
		delete: false,
		upload: true,
		export: false,
	},
	invoice: {
		view: true,
		create: true,
		edit: true,
		delete: false,
		finalize: false,
		manageAufmass: true,
		manageSupplierInfo: true,
	},
	ticketCategories: {
		view: true,
		create: true,
		edit: true,
		delete: false,
		download: true,
		categoryAccess: CATEGORY_ACCESS.ALL,
	},
	tickets: {
		view: TICKET_ACCESS_LEVEL.ALL,
		edit: TICKET_ACCESS_LEVEL.ALL,
		create: true,
		delete: false,
		approve: false,
	},
	// members: only `view` is delegatable. Every grant action (add, invite,
	// editPermissions, remove) is Project-Admin-only and intentionally absent
	// from the shape so it can never be granted via a preset.
	members: { view: true },
	// companies: view plus delegatable create / edit / delete (default off).
	companies: { view: true, create: false, edit: false, delete: false },
};

export const PERMISSION_AREA_CONFIG = [
	{
		area: PROJECT_AREA.OVERVIEW,
		labelKey: "projects.permissions.areas.overview",
		overviewLevels: true,
		booleanActions: [PROJECT_ACTION.EDIT],
	},
	{
		area: PROJECT_AREA.PLANNING,
		labelKey: "projects.permissions.areas.planning",
		booleanActions: [
			PROJECT_ACTION.VIEW,
			PROJECT_ACTION.EDIT,
			PROJECT_ACTION.EXPORT,
		],
	},
	{
		area: PROJECT_AREA.COST_TRACKING,
		labelKey: "projects.permissions.areas.costTracking",
		booleanActions: [
			PROJECT_ACTION.VIEW,
			PROJECT_ACTION.EDIT,
			PROJECT_ACTION.EXPORT,
		],
	},
	{
		area: PROJECT_AREA.LV,
		labelKey: "projects.permissions.areas.lv",
		booleanActions: [
			PROJECT_ACTION.VIEW,
			PROJECT_ACTION.CREATE,
			PROJECT_ACTION.EDIT,
			PROJECT_ACTION.DELETE,
			PROJECT_ACTION.UPLOAD,
			PROJECT_ACTION.EXPORT,
		],
	},
	{
		area: PROJECT_AREA.INVOICE,
		labelKey: "projects.permissions.areas.invoice",
		booleanActions: [
			PROJECT_ACTION.VIEW,
			PROJECT_ACTION.CREATE,
			PROJECT_ACTION.EDIT,
			PROJECT_ACTION.MANAGE_AUFMASS,
			PROJECT_ACTION.MANAGE_SUPPLIER_INFO,
			PROJECT_ACTION.FINALIZE,
			PROJECT_ACTION.DELETE,
		],
	},
	{
		area: PROJECT_AREA.TICKET_CATEGORIES,
		labelKey: "projects.permissions.areas.ticketCategories",
		booleanActions: [
			PROJECT_ACTION.VIEW,
			PROJECT_ACTION.CREATE,
			PROJECT_ACTION.EDIT,
			PROJECT_ACTION.DELETE,
			PROJECT_ACTION.DOWNLOAD,
		],
		categoryAccess: true,
	},
	{
		area: PROJECT_AREA.TICKETS,
		labelKey: "projects.permissions.areas.tickets",
		ticketLevels: true,
		booleanActions: [
			PROJECT_ACTION.CREATE,
			PROJECT_ACTION.DELETE,
			PROJECT_ACTION.APPROVE,
		],
	},
	{
		area: PROJECT_AREA.MEMBERS,
		labelKey: "projects.permissions.areas.members",
		// Only `view` is grantable - all member management is Project-Admin-only.
		booleanActions: [PROJECT_ACTION.VIEW],
	},
	{
		area: PROJECT_AREA.COMPANIES,
		labelKey: "projects.permissions.areas.companies",
		booleanActions: [
			PROJECT_ACTION.VIEW,
			PROJECT_ACTION.CREATE,
			PROJECT_ACTION.EDIT,
			PROJECT_ACTION.DELETE,
		],
	},
];

export const clonePermissions = (permissions) =>
	JSON.parse(JSON.stringify(permissions ?? {}));

export const createDefaultPermissions = () => clonePermissions(MEMBER_PERMISSIONS);

const mergeArea = (defaults, source) => ({
	...defaults,
	...(source && typeof source === "object" ? source : {}),
});

/**
 * Enforce the view-dependency invariant: `view` is the access gate for an area,
 * and every other action is reachable only from a screen you can open with
 * `view`. So an action without `view` is an incoherent grant (the user could be
 * "allowed" to create but can never see the screen to do it). This clears every
 * dependent action whenever its area's view is off, making such a set impossible
 * to express. Idempotent and pure - safe to run on every change and on save.
 *
 * - boolean-view areas (lv, invoice, costTracking, planning, ticketCategories,
 *   members, companies): `view !== true` ⇒ all other boolean actions → false.
 * - tickets: `view` is a level; `view === "NONE"` (no read access) ⇒ edit → NONE
 *   and create/delete/approve → false.
 * - overview: `view` is a level that is always present (OVERVIEW minimum), so
 *   there is nothing to cascade.
 * - categoryAccess (a trade scope, not a screen action) is left untouched.
 *
 * @param {ProjectPermissions} permissions - Permission snapshot to sanitize.
 * @returns {ProjectPermissions} A coherent copy (input is not mutated).
 */
export const sanitizePermissions = (permissions) => {
	if (!permissions || typeof permissions !== "object") {
		return permissions;
	}
	const result = clonePermissions(permissions);
	PERMISSION_AREA_CONFIG.forEach((config) => {
		const area = result[config.area];
		if (!area || typeof area !== "object") return;

		if (config.ticketLevels) {
			if (area.view === TICKET_ACCESS_LEVEL.NONE) {
				area.edit = TICKET_ACCESS_LEVEL.NONE;
				area.create = false;
				area.delete = false;
				area.approve = false;
			}
			return;
		}

		if (config.overviewLevels) return;

		if (
			config.booleanActions?.includes(PROJECT_ACTION.VIEW) &&
			area.view !== true
		) {
			config.booleanActions.forEach((action) => {
				if (action !== PROJECT_ACTION.VIEW) area[action] = false;
			});
		}
	});
	return result;
};

export const normalizePermissions = (permissions) => {
	const base = createDefaultPermissions();
	if (!permissions || typeof permissions !== "object") {
		return base;
	}
	const result = { ...base };
	Object.keys(base).forEach((area) => {
		result[area] = mergeArea(base[area], permissions[area]);
	});
	return sanitizePermissions(result);
};

export const permissionsEqual = (a, b) =>
	JSON.stringify(normalizePermissions(a)) ===
	JSON.stringify(normalizePermissions(b));

export const isCategoryAccessScoped = (categoryAccess) =>
	Array.isArray(categoryAccess);

export const getSelectedCategoryIds = (categoryAccess) =>
	isCategoryAccessScoped(categoryAccess) ? categoryAccess : [];

/**
 * True when trade access is set to "Selected trades" (SCOPED) but no trade is
 * actually selected. This is an incomplete state, not a meaningful one: it
 * neither grants specific trades nor is it the explicit "No trades" choice, so
 * persisting it would be ambiguous. The form allows it transiently (so a user
 * can clear-all and re-pick) but save sites use this to block the Save button
 * until a trade is picked or the scope is switched. Gated on `view` because the
 * trade control only exists once the ticket-categories area is viewable.
 */
export const isTradeAccessIncomplete = (permissions) => {
	const cat = permissions?.ticketCategories;
	return (
		cat?.view === true &&
		isCategoryAccessScoped(cat.categoryAccess) &&
		cat.categoryAccess.length === 0
	);
};

/** UI value for category scope dropdown: ALL | NONE | SCOPED */
export const getCategoryAccessScope = (categoryAccess) => {
	if (categoryAccess === CATEGORY_ACCESS.NONE) return CATEGORY_ACCESS.NONE;
	if (isCategoryAccessScoped(categoryAccess)) return "SCOPED";
	return CATEGORY_ACCESS.ALL;
};

export const formatCategoryAccessSummary = (permissions, t) => {
	const access = permissions?.ticketCategories?.categoryAccess;
	if (access === CATEGORY_ACCESS.NONE) {
		return t("projects.permissions.category-access-none");
	}
	if (access === CATEGORY_ACCESS.ALL) {
		return t("projects.permissions.category-access-all");
	}
	if (isCategoryAccessScoped(access)) {
		return t("projects.permissions.category-access-scoped", {
			count: access.length,
		});
	}
	return t("projects.permissions.category-access-all");
};

export const actionLabelKey = (action) =>
	`projects.permissions.actions.${action}`;
